import { faExclamationTriangle, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { SegmentedMessage } from 'sms-segments-calculator';

import { Color } from '@/constants/color';
import { SMS_MAX_LENGTH } from '@/constants/messageLimits';
import { Message } from '@/types/message';
import { Banner } from '@/ui';
import Button from '@/ui/components/Button';
import EmojiPickerButton from '@/ui/components/EmojiPickerButton';
import InputField from '@/ui/components/Form/InputField';

const URL_REGEX = /(^|[^\w@])(https?:\/\/)?([\w-]+\.)+[a-z]{2,}(\/\S*)?/i;

interface Props {
  draftMessage: string;
  isLoading: boolean;
  placeholder: string;
  replyToMessage: Message | null;
  smsDisabled?: boolean;
  onChange: (value: string) => void;
  onDismissReply: () => void;
  onSubmit: () => void;
}

const SmsComposer = ({
  draftMessage,
  isLoading,
  placeholder,
  replyToMessage,
  smsDisabled = false,
  onChange,
  onDismissReply,
  onSubmit
}: Props) => {
  const hasLink = URL_REGEX.test(draftMessage);
  const isOverLimit = draftMessage.length > SMS_MAX_LENGTH;
  const inputRef = useRef<HTMLInputElement>(null);

  const segmentedMessage = useMemo(() => new SegmentedMessage(draftMessage), [draftMessage]);

  const error = isOverLimit
    ? `Your message exceeds the ${SMS_MAX_LENGTH} character limit and cannot be sent.`
    : null;

  const warning = useMemo(() => {
    if (hasLink) {
      return 'Please remove the link in your message, links are often flagged as spam by carriers.';
    }
    if (segmentedMessage.segmentsCount > 1) {
      return 'Your message is too long, try to keep your text messages short for easier readability';
    }
    return null;
  }, [hasLink, segmentedMessage]);

  const [confirming, setConfirming] = useState(false);

  useEffect(() => setConfirming(false), [draftMessage]);

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event?.target?.value ?? '');
    },
    [onChange]
  );

  const handleEmojiClick = useCallback(
    (emoji: string) => {
      const el = inputRef.current;
      const start = el?.selectionStart ?? draftMessage.length;
      const end = el?.selectionEnd ?? draftMessage.length;
      onChange(draftMessage.slice(0, start) + emoji + draftMessage.slice(end));
      // Wait for the controlled value to re-render before moving the caret
      requestAnimationFrame(() => {
        const caret = start + emoji.length;
        el?.focus();
        el?.setSelectionRange(caret, caret);
      });
    },
    [draftMessage, onChange]
  );

  const handleSubmit = useCallback(
    (event: React.SyntheticEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (smsDisabled || !draftMessage || isOverLimit) return;

      if (!warning) {
        onSubmit();
        return;
      }

      if (confirming) {
        setConfirming(false);
        onSubmit();
      } else {
        setConfirming(true);
      }
    },
    [confirming, draftMessage, onSubmit, warning, smsDisabled, isOverLimit]
  );

  return (
    <div>
      {replyToMessage && (
        <div className="flex items-center justify-between mb-1 px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <span className="text-blue-700 truncate">
            <span className="font-medium">Replying to:</span>{' '}
            <span className="text-blue-600">{replyToMessage.preview}</span>
          </span>
          <button
            type="button"
            aria-label="Dismiss reply"
            onClick={onDismissReply}
            className="ml-2 flex-shrink-0 text-blue-400 hover:text-blue-600">
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
      {smsDisabled && (
        <Banner
          color="red"
          icon={faExclamationTriangle}
          label="This person has opted out of SMS messages."
        />
      )}
      <form className="flex items-center gap-2" onSubmit={handleSubmit}>
        <div className="relative flex-1">
          <InputField
            id="message-body"
            ref={inputRef}
            type="text"
            placeholder={placeholder}
            value={draftMessage}
            wrapperClassNames="w-full"
            disabled={smsDisabled}
            onChange={handleChange}
          />
        </div>
        <EmojiPickerButton
          className="text-xl px-1 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={smsDisabled}
          onEmojiClick={handleEmojiClick}
        />
        <Button
          type="submit"
          color={confirming ? Color.AMBER : Color.BLUE}
          disabled={!draftMessage || smsDisabled || isOverLimit}
          loading={isLoading}>
          {confirming ? 'You sure?' : 'Send'}
        </Button>
      </form>
      {error && <div className="mt-1 px-1 text-xs text-red-600 text-right">{error}</div>}
      {!error && confirming && warning && (
        <div className="mt-1 px-1 text-xs text-amber-600 text-right">{warning}</div>
      )}
    </div>
  );
};

export default SmsComposer;
